if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initMobileFixedForm();
        this.initFakeBISTrigger();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          // Update button state - check both normal and fixed form
          const submitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const isDisabled = submitButton?.hasAttribute('disabled') ?? true;
          const buttonText = submitButton?.querySelector('span')?.textContent || 
                            (isDisabled ? window.variantStrings.soldOut : window.variantStrings.addToCart);
          
          // Duplicate buttons container to fixed form on variant change
          this.duplicateButtonsToFixedForm();
          
          // Update both forms if they exist
          const productForm = this.querySelector(`product-form`);
          const fixedForm = document.getElementById('mobile-fixed-product-form')?.querySelector(`product-form`);
          
          // Handle normal form
          if (productForm) {
            const buttonsContainer = productForm.querySelector('.product-form__buttons');
            if (buttonsContainer) {
              const formButton = productForm.querySelector('[type="submit"]');
              const klaviyoBisTrigger = buttonsContainer.querySelector('.klaviyo-bis-trigger');
              
              if (isDisabled) {
                // Hide normal button and show Klaviyo BIS trigger
                if (formButton) {
                  formButton.style.display = 'none';
                  formButton.setAttribute('disabled', 'disabled');
                  formButton.setAttribute('aria-disabled', 'true');
                }
                
                // Show Klaviyo BIS trigger if it exists
                if (klaviyoBisTrigger) {
                  klaviyoBisTrigger.style.display = '';
                } else {
                  // Wait for Klaviyo to inject the button
                  this.waitForKlaviyoBIS(buttonsContainer, formButton);
                }
              } else {
                // Show normal button and hide Klaviyo BIS trigger
                if (formButton) {
                  formButton.style.display = '';
                  formButton.removeAttribute('disabled');
                  formButton.removeAttribute('aria-disabled');
                }
                
                if (klaviyoBisTrigger) {
                  klaviyoBisTrigger.style.display = 'none';
                }
              }
            }
          }
          
          // Handle fixed mobile form - sync with original
          if (fixedForm) {
            const buttonsContainer = fixedForm.querySelector('.product-form__buttons');
            if (buttonsContainer) {
              const formButton = fixedForm.querySelector('[type="submit"]');
              const klaviyoBisTrigger = buttonsContainer.querySelector('.klaviyo-bis-trigger');
              
              if (isDisabled) {
                // Hide normal button and show Klaviyo BIS trigger
                if (formButton) {
                  formButton.style.display = 'none';
                  formButton.setAttribute('disabled', 'disabled');
                  formButton.setAttribute('aria-disabled', 'true');
                }
                
                // Show Klaviyo BIS trigger if it exists
                if (klaviyoBisTrigger) {
                  klaviyoBisTrigger.style.display = '';
                }
              } else {
                // Show normal button and hide Klaviyo BIS trigger
                if (formButton) {
                  formButton.style.display = '';
                  formButton.removeAttribute('disabled');
                  formButton.removeAttribute('aria-disabled');
                }
                
                if (klaviyoBisTrigger) {
                  klaviyoBisTrigger.style.display = 'none';
                }
              }
            }
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        // Get all product forms including the one in fixed container
        const productFormSelectors = [
          ...this.querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
          ),
          ...(document.getElementById('mobile-fixed-product-form')?.querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
          ) || [])
        ];

        productFormSelectors.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        // Duplicate buttons container to fixed form
        this.duplicateButtonsToFixedForm();
        
        // Update both forms if they exist
        const productForm = this.querySelector(`product-form`);
        const fixedForm = document.getElementById('mobile-fixed-product-form')?.querySelector(`product-form`);
        
        // Handle normal form
        if (productForm) {
          productForm.toggleSubmitButton(true, window.variantStrings.unavailable);
          
          const buttonsContainer = productForm.querySelector('.product-form__buttons');
          if (buttonsContainer) {
            const formButton = productForm.querySelector('[type="submit"]');
            const klaviyoBisTrigger = buttonsContainer.querySelector('.klaviyo-bis-trigger');
            
            // Hide normal button and show Klaviyo BIS trigger
            if (formButton) {
              formButton.style.display = 'none';
              formButton.setAttribute('disabled', 'disabled');
              formButton.setAttribute('aria-disabled', 'true');
            }
            
            if (klaviyoBisTrigger) {
              klaviyoBisTrigger.style.display = '';
            } else {
              // Wait for Klaviyo to inject the button
              this.waitForKlaviyoBIS(buttonsContainer, formButton);
            }
          }
        }
        
        // Handle fixed mobile form - sync with original
        if (fixedForm) {
          const buttonsContainer = fixedForm.querySelector('.product-form__buttons');
          if (buttonsContainer) {
            const formButton = fixedForm.querySelector('[type="submit"]');
            const klaviyoBisTrigger = buttonsContainer.querySelector('.klaviyo-bis-trigger');
            
            // Hide normal button and show Klaviyo BIS trigger
            if (formButton) {
              formButton.style.display = 'none';
              formButton.setAttribute('disabled', 'disabled');
              formButton.setAttribute('aria-disabled', 'true');
            }
            
            if (klaviyoBisTrigger) {
              klaviyoBisTrigger.style.display = '';
            }
          }
        }

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        // Check both in product-info and in fixed container
        return this.querySelector(`product-form`) || 
               document.getElementById('mobile-fixed-product-form')?.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      waitForKlaviyoBIS(buttonsContainer, formButton) {
        // Wait for Klaviyo to inject the BIS trigger button
        let attempts = 0;
        const maxAttempts = 20; // Check for 2 seconds (20 * 100ms)
        
        const checkForKlaviyoBIS = setInterval(() => {
          attempts++;
          const klaviyoBisTrigger = buttonsContainer.querySelector('.klaviyo-bis-trigger');
          
          if (klaviyoBisTrigger) {
            // Klaviyo BIS button found, show it and hide normal button
            klaviyoBisTrigger.style.display = '';
            if (formButton) {
              formButton.style.display = 'none';
            }
            clearInterval(checkForKlaviyoBIS);
          } else if (attempts >= maxAttempts) {
            // Stop checking after max attempts
            clearInterval(checkForKlaviyoBIS);
          }
        }, 100);
      }

      initMobileFixedForm() {
        // Check if we're on mobile
        const isMobile = () => window.innerWidth <= 768;
        
        // Debounce timer for duplication
        let duplicateTimer = null;
        let lastDuplicateTime = 0;
        const MIN_DUPLICATE_INTERVAL = 500; // Minimum 500ms between duplications
        
        const debouncedDuplicate = () => {
          const now = Date.now();
          if (now - lastDuplicateTime < MIN_DUPLICATE_INTERVAL) {
            return; // Too soon, skip
          }
          
          clearTimeout(duplicateTimer);
          duplicateTimer = setTimeout(() => {
            if (isMobile() && !this.isDuplicating) {
              lastDuplicateTime = Date.now();
              this.duplicateButtonsToFixedForm();
              this.updateButtonVisibility();
            }
          }, 200);
        };
        
        // Initial duplicate
        debouncedDuplicate();
        
        // Check button visibility on page load
        setTimeout(() => {
          this.updateButtonVisibility();
        }, 200);

        // Watch for Klaviyo injecting the BIS button
        const productForm = this.productForm;
        if (productForm) {
          const buttonsContainer = productForm.querySelector('.product-form__buttons');
          if (buttonsContainer) {
            const klaviyoObserver = new MutationObserver((mutations) => {
              // Only re-duplicate if a new Klaviyo element was added
              const hasNewKlaviyoElement = mutations.some(mutation => 
                mutation.addedNodes.length > 0 && 
                Array.from(mutation.addedNodes).some(node => 
                  node.nodeType === 1 && 
                  (node.classList?.contains('klaviyo-bis-trigger') || 
                   node.querySelector?.('.klaviyo-bis-trigger'))
                )
              );
              
              if (hasNewKlaviyoElement && isMobile() && !this.isDuplicating) {
                debouncedDuplicate();
              }
            });

            klaviyoObserver.observe(buttonsContainer, {
              childList: true,
              subtree: true
            });
          }
        }
        
        // Watch for buttons being removed from fixed form and re-add them
        // But only if they're actually missing, not just being updated
        let fixedFormContainer = document.getElementById('mobile-fixed-product-form');
        if (!fixedFormContainer) {
          fixedFormContainer = document.createElement('div');
          fixedFormContainer.id = 'mobile-fixed-product-form';
          document.body.appendChild(fixedFormContainer);
        }
        
        const fixedFormObserver = new MutationObserver((mutations) => {
          if (!isMobile() || this.isDuplicating) return;
          
          // Check if buttons container is actually missing
          const fixedProductForm = fixedFormContainer.querySelector('product-form');
          const hasButtons = fixedProductForm?.querySelector('.product-form__buttons');
          
          if (!hasButtons) {
            // Buttons are actually missing, re-duplicate
            debouncedDuplicate();
          }
        });
        
        fixedFormObserver.observe(fixedFormContainer, {
          childList: true,
          subtree: true
        });

        // Handle resize
        let resizeTimeout;
        const handleResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            if (isMobile() && !this.isDuplicating) {
              debouncedDuplicate();
            }
          }, 100);
        };
        window.addEventListener('resize', handleResize);

        // Handle content updates (when variant changes or form is re-rendered)
        // Only observe the product-form element, not the entire product-info
        const observer = new MutationObserver((mutations) => {
          if (!isMobile() || this.isDuplicating) return;
          
          // Only trigger if buttons container was actually added/removed from product-form
          const hasRelevantChanges = mutations.some(mutation => {
            if (mutation.addedNodes.length > 0) {
              return Array.from(mutation.addedNodes).some(node => {
                if (node.nodeType !== 1) return false;
                // Only trigger if product-form__buttons is added to product-form
                return node.classList?.contains('product-form__buttons') ||
                       (node.tagName === 'PRODUCT-FORM' && node.querySelector('.product-form__buttons'));
              });
            }
            return false;
          });
          
          if (hasRelevantChanges) {
            debouncedDuplicate();
          }
        });

        // Only observe the product-form, not the entire product-info
        if (productForm) {
          observer.observe(productForm, {
            childList: true,
            subtree: false // Only direct children, not deep subtree
          });
        }

        // Also listen for product-info:loaded event in case form is re-rendered
        this.addEventListener('product-info:loaded', () => {
          setTimeout(() => {
            if (isMobile() && !this.isDuplicating) {
              debouncedDuplicate();
            }
          }, 50);
        });
      }

      duplicateButtonsToFixedForm() {
        // Check if we're on mobile
        const isMobile = () => window.innerWidth <= 768;
        if (!isMobile()) return;

        // Prevent re-duplication during button clicks or if already duplicating
        if (this.isDuplicating) return;
        this.isDuplicating = true;

        try {
          const productForm = this.productForm;
          if (!productForm) {
            this.isDuplicating = false;
            return;
          }

          const originalButtonsContainer = productForm.querySelector('.product-form__buttons');
          if (!originalButtonsContainer) {
            this.isDuplicating = false;
            return;
          }

          let fixedFormContainer = document.getElementById('mobile-fixed-product-form');
          if (!fixedFormContainer) {
            fixedFormContainer = document.createElement('div');
            fixedFormContainer.id = 'mobile-fixed-product-form';
            document.body.appendChild(fixedFormContainer);
          }

          // Create product-form wrapper for fixed container
          let fixedProductForm = fixedFormContainer.querySelector('product-form');
          if (!fixedProductForm) {
            fixedProductForm = document.createElement('product-form');
            fixedProductForm.className = productForm.className;
            fixedFormContainer.appendChild(fixedProductForm);
          }

          // Check if we actually need to duplicate (compare content)
          const existingCloned = fixedProductForm.querySelector('.product-form__buttons');
          if (existingCloned) {
            // Compare if content is the same to avoid unnecessary duplication
            const originalHTML = originalButtonsContainer.innerHTML.trim();
            const clonedHTML = existingCloned.innerHTML.trim();
            
            // Only re-duplicate if content actually changed
            if (originalHTML === clonedHTML) {
              this.isDuplicating = false;
              return;
            }
            
            // Remove existing cloned container
            existingCloned.remove();
          }

          // Clone the buttons container with deep clone to include all elements
          const clonedButtonsContainer = originalButtonsContainer.cloneNode(true);
          
          // Add cloned container to fixed form
          fixedProductForm.appendChild(clonedButtonsContainer);
          
          // Setup click handlers for cloned buttons
          this.setupClonedButtonsHandlers(clonedButtonsContainer, originalButtonsContainer);
        } finally {
          this.isDuplicating = false;
        }
      }

      setupClonedButtonsHandlers(clonedContainer, originalContainer) {
        // Remove any existing listeners by cloning the container
        const newClonedContainer = clonedContainer.cloneNode(true);
        clonedContainer.parentNode.replaceChild(newClonedContainer, clonedContainer);
        
        // Setup submit button to use original form
        const clonedSubmitButton = newClonedContainer.querySelector('[type="submit"]');
        const originalForm = originalContainer.closest('form');
        
        if (clonedSubmitButton && originalForm) {
          // Use event delegation on the container to handle clicks
          newClonedContainer.addEventListener('click', (e) => {
            if (e.target === clonedSubmitButton || e.target.closest('[type="submit"]') === clonedSubmitButton) {
              e.preventDefault();
              e.stopPropagation();
              
              // Trigger submit on original form
              const originalSubmitButton = originalContainer.querySelector('[type="submit"]');
              if (originalSubmitButton && !originalSubmitButton.hasAttribute('disabled')) {
                originalSubmitButton.click();
              }
              
              // Don't re-duplicate immediately to avoid loops
              // Only re-duplicate if buttons are actually removed
            }
          });
        }
        
        // Setup Klaviyo BIS trigger to use original
        const clonedBisTrigger = newClonedContainer.querySelector('.klaviyo-bis-trigger');
        
        if (clonedBisTrigger) {
          // Use event delegation
          newClonedContainer.addEventListener('click', (e) => {
            if (e.target === clonedBisTrigger || e.target.closest('.klaviyo-bis-trigger') === clonedBisTrigger) {
              e.preventDefault();
              e.stopPropagation();
              
              // Click the original Klaviyo BIS trigger if it exists
              const currentOriginalBisTrigger = originalContainer.querySelector('.klaviyo-bis-trigger');
              if (currentOriginalBisTrigger) {
                if (currentOriginalBisTrigger.click) {
                  currentOriginalBisTrigger.click();
                } else {
                  const clickEvent = new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                  });
                  currentOriginalBisTrigger.dispatchEvent(clickEvent);
                }
              }
              
              // Don't re-duplicate immediately to avoid loops
            }
          });
        }
      }

      waitForOriginalBISTrigger(clonedContainer, originalContainer) {
        let attempts = 0;
        const maxAttempts = 50;
        
        const checkForOriginalBIS = setInterval(() => {
          attempts++;
          const originalBisTrigger = originalContainer.querySelector('.klaviyo-bis-trigger');
          const clonedBisTrigger = clonedContainer.querySelector('.klaviyo-bis-trigger');
          
          if (originalBisTrigger && clonedBisTrigger) {
            // Setup click handler
            clonedBisTrigger.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (originalBisTrigger.click) {
                originalBisTrigger.click();
              }
            });
            clearInterval(checkForOriginalBIS);
          } else if (attempts >= maxAttempts) {
            clearInterval(checkForOriginalBIS);
          }
        }, 100);
      }

      initFakeBISTrigger() {
        // No longer needed - we're duplicating the entire buttons container
      }

      updateButtonVisibility() {
        // Check button visibility on page load and update accordingly
        const productForm = this.querySelector(`product-form`);
        const fixedForm = document.getElementById('mobile-fixed-product-form')?.querySelector(`product-form`);
        
        if (!productForm) return;
        
        // Check if current variant is out of stock
        const submitButton = productForm.querySelector('[type="submit"]');
        const isDisabled = submitButton?.hasAttribute('disabled') || submitButton?.getAttribute('aria-disabled') === 'true';
        
        // Update original form
        const buttonsContainer = productForm.querySelector('.product-form__buttons');
        if (buttonsContainer) {
          const formButton = productForm.querySelector('[type="submit"]');
          const klaviyoBisTrigger = buttonsContainer.querySelector('.klaviyo-bis-trigger');
          
          if (isDisabled) {
            if (formButton) formButton.style.display = 'none';
            if (klaviyoBisTrigger) klaviyoBisTrigger.style.display = '';
          } else {
            if (formButton) formButton.style.display = '';
            if (klaviyoBisTrigger) klaviyoBisTrigger.style.display = 'none';
          }
        }
        
        // Update fixed form (duplicated)
        if (fixedForm) {
          const fixedButtonsContainer = fixedForm.querySelector('.product-form__buttons');
          if (fixedButtonsContainer) {
            const fixedFormButton = fixedForm.querySelector('[type="submit"]');
            const fixedKlaviyoBisTrigger = fixedButtonsContainer.querySelector('.klaviyo-bis-trigger');
            
            if (isDisabled) {
              if (fixedFormButton) fixedFormButton.style.display = 'none';
              if (fixedKlaviyoBisTrigger) fixedKlaviyoBisTrigger.style.display = '';
            } else {
              if (fixedFormButton) fixedFormButton.style.display = '';
              if (fixedKlaviyoBisTrigger) fixedKlaviyoBisTrigger.style.display = 'none';
            }
          }
        }
      }

      waitForRealBISTrigger(buttonsContainer) {
        let attempts = 0;
        const maxAttempts = 50; // Increased to 5 seconds to wait for Klaviyo
        
        const checkForRealBIS = setInterval(() => {
          attempts++;
          const realBisTrigger = buttonsContainer.querySelector('.klaviyo-bis-trigger');
          
          if (realBisTrigger) {
            // Try multiple methods to trigger the click
            setTimeout(() => {
              // Method 1: Direct click
              if (typeof realBisTrigger.click === 'function') {
                realBisTrigger.click();
              }
              
              // Method 2: Dispatch mouse event
              const mouseEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
                buttons: 1
              });
              realBisTrigger.dispatchEvent(mouseEvent);
              
              // Method 3: Dispatch pointer event (more modern)
              if (typeof PointerEvent !== 'undefined') {
                const pointerEvent = new PointerEvent('click', {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                  pointerId: 1
                });
                realBisTrigger.dispatchEvent(pointerEvent);
              }
              
              // Method 4: Try to find and click any nested button/link
              const nestedButton = realBisTrigger.querySelector('button, a');
              if (nestedButton && typeof nestedButton.click === 'function') {
                nestedButton.click();
              }
            }, 50);
            
            clearInterval(checkForRealBIS);
          } else if (attempts >= maxAttempts) {
            clearInterval(checkForRealBIS);
            // If still not found, try to trigger Klaviyo manually
            if (typeof window.klaviyo !== 'undefined') {
              try {
                if (window.klaviyo.push) {
                  window.klaviyo.push(['track', 'Back in Stock']);
                }
                if (window.klaviyo.enable && typeof window.klaviyo.enable === 'function') {
                  window.klaviyo.enable("backinstock");
                }
              } catch (e) {
                console.log('Klaviyo BIS fallback:', e);
              }
            }
          }
        }, 100);
      }

    }
  );
}
